import React from "react";
import {
  Box,
  Button,
  Dialog,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { useLocation, useNavigate } from "react-router-dom";
import useThemeStore from "@/shared/stores/useThemeStore";

type DeckCardGuestLoginDialogProps = {
  open: boolean;
  onClose: () => void;
};

const DeckCardGuestLoginDialog: React.FC<DeckCardGuestLoginDialogProps> = ({
  open,
  onClose,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { darkMode } = useThemeStore();

  const text = darkMode ? "var(--neutral-100)" : "var(--neutral-900)";
  const muted = darkMode ? "var(--neutral-400)" : "var(--neutral-600)";
  const paper = darkMode ? "var(--neutral-900)" : "#fff";
  const border = darkMode ? "var(--neutral-700)" : "var(--neutral-200)";
  const accent = darkMode ? "var(--primary-300)" : "var(--primary-700)";
  const accentSoft = darkMode
    ? "rgba(99, 102, 241, 0.14)"
    : "var(--primary-100)";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "20px",
          bgcolor: paper,
          border: `1px solid ${border}`,
          backgroundImage: `radial-gradient(circle at 50% 0%, ${accentSoft} 0%, transparent 55%)`,
          overflow: "hidden",
        },
      }}
    >
      <IconButton
        onClick={onClose}
        aria-label="Close"
        sx={{
          position: "absolute",
          top: 10,
          right: 10,
          color: muted,
        }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          py: { xs: 4, sm: 5 },
          px: { xs: 2.5, sm: 4 },
        }}
      >
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: "50%",
            display: "grid",
            placeItems: "center",
            mb: 2,
            bgcolor: accentSoft,
            border: `1px solid ${border}`,
          }}
        >
          <LockOutlinedIcon sx={{ fontSize: 26, color: accent }} />
        </Box>

        <Typography
          sx={{
            fontFamily: '"Lexend", sans-serif',
            fontSize: { xs: "1.35rem", sm: "1.5rem" },
            fontWeight: 600,
            color: text,
            mb: 1,
            maxWidth: 360,
            lineHeight: 1.25,
          }}
        >
          Sign in to open this card
        </Typography>

        <Typography
          sx={{
            maxWidth: 380,
            fontSize: "0.9375rem",
            lineHeight: 1.65,
            color: muted,
            mb: 3,
          }}
        >
          Log in to browse cards and open full deck experiences.
        </Typography>

        <Button
          variant="contained"
          disableElevation
          onClick={() =>
            navigate("/sign-in", {
              state: {
                redirect: location.pathname + location.search,
              },
            })
          }
          sx={{
            borderRadius: "999px",
            px: 3,
            py: 1.1,
            textTransform: "none",
            fontWeight: 600,
            fontSize: "0.9rem",
            background: "var(--button-gradient)",
            color: "#fff",
            "&:hover": {
              background: "var(--button-gradient-hover)",
            },
          }}
        >
          Sign in
        </Button>
      </Box>
    </Dialog>
  );
};

export default DeckCardGuestLoginDialog;
